import React from 'react';

import { Form, Input } from '../utils/inputs';
import { InputField } from '../products/helpers';
import { Select } from '../utils/form/fields/Select';
import { useAddProduct } from './hooks/useAddProduct';
import BreadCrumbs from '../utils/BreadCrumbs';

export const AddProductPage: React.FC = () => {
    const {
        categories,
        handleSubmit,
        handleChange,
        handleMultiSelectChange,
        handleFileChange,
        handleMaxAllowedQuantityToggle,
        product,
        units,
        quantityOptions,
        limitationGroups,
        isLoading,
        imagePreviewUrl,
    } = useAddProduct();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    if (isLoading) return <div>Loading...</div>;

    const selectedGroupValues = product.groups
        ? limitationGroups.filter((group) => product.groups?.includes(group.value))
        : [];

    return (
        <>
            <BreadCrumbs path="/products/add-product" />
            <div>
                <h3>Add New Product</h3>
                <Form className="wfp-form" onSubmit={handleSubmit}>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Name</label>
                        <InputField
                            autoFocused={true}
                            maxLength={35}
                            name="name"
                            onChange={handleChange}
                            readonly={false}
                            required={true}
                            type="text"
                            value={product.name || ''}
                        />
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Category</label>
                        <div className="replaced-form-input col-sm-8">
                            <Select
                                name="category"
                                onChange={handleChange}
                                options={categories}
                                placeholder="Select category (required)"
                                value={product.category}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Quantity Option</label>
                        <div className="replaced-form-input col-sm-8">
                            <Select
                                disabled={product.maxAllowedQuantity === 1}
                                name="quantityOption"
                                onChange={handleChange}
                                options={quantityOptions}
                                placeholder="Select quantity option (required)"
                                value={product.quantityOption}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Unit</label>
                        <div className="replaced-form-input col-sm-8">
                            <Select
                                name="unit"
                                onChange={handleChange}
                                options={units}
                                placeholder="Select unit (required)"
                                value={product.unit}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Allow only 1 quantity</label>
                        <div className="col-sm-8">
                            <label className="wfp-form">
                                <input
                                    checked={product.maxAllowedQuantity === 1}
                                    className="wfp-checkbox"
                                    onChange={(e) => handleMaxAllowedQuantityToggle(e.target.checked)}
                                    type="checkbox"
                                />
                            </label>
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Groups</label>
                        <div className="replaced-form-input col-sm-8">
                            <Select
                                multi={true}
                                name="productLimitationGroups"
                                onChange={handleMultiSelectChange}
                                options={limitationGroups}
                                placeholder="Select groups (required)"
                                value={selectedGroupValues}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Translation</label>
                        <div className="col-sm-8">
                            <Input
                                className="form-control"
                                maxLength={100}
                                name="translation"
                                onChange={handleChange}
                                placeholder="Optional - translated name"
                                required={false}
                                type="text"
                                value={product.translation || ''}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Translation (English)</label>
                        <div className="col-sm-8">
                            <Input
                                className="form-control"
                                maxLength={100}
                                name="translationEn"
                                onChange={handleChange}
                                placeholder="Optional - English translated name"
                                required={false}
                                type="text"
                                value={product.translationEn || ''}
                            />
                        </div>
                    </div>
                    <div className="wfp-form--group row">
                        <label className="col-sm-4 ta-right">Product Image</label>
                        <div className="col-sm-8">
                            <input
                                accept="image/*"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                type="file"
                            />
                            <button
                                className="wfp-btn--primary"
                                onClick={() => fileInputRef.current?.click()}
                                type="button"
                            >
                                Choose Image
                            </button>
                            <small className="help-block" style={{ display: 'block', marginTop: 8 }}>
                                Upload an image for this product (optional)
                            </small>
                            {imagePreviewUrl && (
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                                    <img
                                        alt="New product preview"
                                        src={imagePreviewUrl}
                                        style={{
                                            maxWidth: 300,
                                            maxHeight: 300,
                                            objectFit: 'contain',
                                            border: '1px solid #ddd',
                                            borderRadius: 4,
                                            padding: 8,
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="wfp-form--actions text-center">
                        <div className="col-sm-12">
                            <button className="wfp-btn--primary" type="submit">
                                Park
                            </button>
                        </div>
                    </div>
                </Form>
            </div>
        </>
    );
};
